import React from "react";
import {
  Search,
  Workflow,
  Link2,
  Mail,
  MessagesSquare,
  CreditCard,
  RefreshCw,
  ShoppingCart,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { BOOK_CALL_URL, WHATSAPP_URL } from "@/lib/constants";

const steps = [
  {
    n: "01",
    title: "We understand",
    body: "Your business, your customers, your sales flow, your current gaps.",
    icon: Search,
  },
  {
    n: "02",
    title: "We design your system",
    body: "Every lead gets a response. Is guided. Is followed up. No drops.",
    icon: Workflow,
  },
  {
    n: "03",
    title: "We integrate everywhere",
    body: "Instagram Ads, website, CRM, payments — nothing slips through.",
    icon: Link2,
  },
];

const journey = [
  {
    stage: "Before they buy",
    icon: ShoppingCart,
    points: [
      "Capture leads directly on WhatsApp",
      "Replace forms with conversations",
    ],
  },
  {
    stage: "When they enquire",
    icon: MessagesSquare,
    points: ["Instant replies, structured responses", "Qualification built-in"],
  },
  {
    stage: "Before they decide",
    icon: Mail,
    points: ["Timed follow-ups", "Nurture messages that warm the lead"],
  },
  {
    stage: "When they’re ready",
    icon: CreditCard,
    points: ["Conversion messaging", "Seamless payment flow"],
  },
  {
    stage: "After they buy",
    icon: RefreshCw,
    points: ["Onboarding", "Re-engagement and repeat offers"],
  },
];

function CTARow({ bookTestId, waTestId }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      <a
        href={BOOK_CALL_URL}
        target="_blank"
        rel="noreferrer"
        data-testid={bookTestId}
        className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm sm:text-base font-semibold transition-all hover:scale-[1.03]"
        style={{ background: "var(--gold)", color: "var(--ink)" }}
      >
        Book a Strategy Call
        <ArrowRight
          size={17}
          className="transition-transform group-hover:translate-x-0.5"
        />
      </a>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        data-testid={waTestId}
        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm sm:text-base font-semibold text-white transition-all hover:scale-[1.03]"
        style={{ background: "var(--whatsapp)" }}
      >
        <MessageCircle size={17} strokeWidth={2.5} />
        Chat on WhatsApp
      </a>
    </div>
  );
}

export default function SystemBlock() {
  return (
    <section
      id="system"
      data-testid="system-section"
      className="relative py-20 lg:py-28"
      style={{ background: "var(--cream)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Introducing */}
        <div className="max-w-4xl">
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.06]"
            data-testid="system-headline"
          >
            Conversio
            <span className="font-display-italic">™</span>{" "}
            <span className="font-display-italic">
              WhatsApp Revenue Multiplier System
            </span>
          </h2>
          <p
            className="mt-6 text-lg lg:text-xl max-w-2xl"
            style={{ color: "var(--text-muted)" }}
          >
            A done-for-you system that transforms your WhatsApp into a revenue
            engine.
          </p>
        </div>

        {/* How it works */}
        <div className="mt-24">
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl max-w-2xl leading-tight">
            How it works —{" "}
            <span className="font-display-italic">in 3 steps.</span>
          </h3>

          <div className="mt-12 grid lg:grid-cols-3 gap-5 lg:gap-6">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  data-testid={`step-${i}`}
                  className="rounded-3xl p-8 bg-white card-hover"
                  style={{
                    border: "1px solid var(--line)",
                    boxShadow:
                      "0 30px 60px -30px rgba(31,35,48,0.15), 0 2px 6px -2px rgba(31,35,48,0.04)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="font-display-italic text-4xl"
                      style={{ color: "var(--gold-dark)" }}
                    >
                      {s.n}
                    </span>
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{
                        background: "var(--cream-soft)",
                        color: "var(--gold-dark)",
                      }}
                    >
                      <Icon size={22} />
                    </div>
                  </div>
                  <h4 className="font-display text-2xl mt-6 text-[var(--ink)]">
                    {s.title}
                  </h4>
                  <p
                    className="mt-3 text-base leading-relaxed"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {s.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Customer journey */}
        <div className="mt-24">
          <div className="max-w-3xl">
            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              From first message to{" "}
              <span className="font-display-italic">repeat purchase</span> —{" "}
              <span
                className="font-display"
                style={{ color: "var(--ink)" }}
              >
                Conversio
                <span
                  className="font-display-italic"
                  style={{ color: "var(--gold-dark)" }}
                >
                  ™
                </span>
              </span>{" "}
              handles the entire journey.
            </h3>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
            {journey.map((j, i) => {
              const Icon = j.icon;
              return (
                <div
                  key={j.stage}
                  data-testid={`journey-${i}`}
                  className="rounded-2xl p-6 bg-white h-full card-hover"
                  style={{
                    border: "1px solid var(--line)",
                    boxShadow: "0 20px 40px -30px rgba(31,35,48,0.12)",
                  }}
                >
                  <div className="flex items-center justify-between mb-5">
                    <span
                      className="text-xs font-bold uppercase tracking-wider"
                      style={{ color: "var(--gold-dark)" }}
                    >
                      0{i + 1}
                    </span>
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{
                        background: "rgba(37,211,102,0.1)",
                        color: "var(--whatsapp-dark)",
                      }}
                    >
                      <Icon size={18} />
                    </div>
                  </div>
                  <h4 className="font-display text-lg text-[var(--ink)] leading-snug">
                    {j.stage}
                  </h4>
                  <ul
                    className="mt-4 space-y-2 text-sm"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {j.points.map((p) => (
                      <li key={p} className="flex gap-2">
                        <span style={{ color: "var(--gold)" }}>→</span>
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-14">
            <CTARow bookTestId="system-cta" waTestId="system-whatsapp-cta" />
          </div>
        </div>
      </div>
    </section>
  );
}
